import React, { useState, useEffect } from 'react';

const OptimizedImage = ({ src, alt, className, width, height, sizes, quality = 0.8, fixedContainer = false }) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [srcset, setSrcset] = useState('');

  useEffect(() => {
    const loadImage = async () => {
      try {
        // Verificar si la imagen está en caché
        const cachedImage = localStorage.getItem(`img_cache_${src}`);
        if (cachedImage) {
          setImageSrc(cachedImage);
          setIsLoading(false);
          return;
        }

        // Si no está en caché, cargar la imagen
        const response = await fetch(src);
        const blob = await response.blob();
        
        // Generar srcset para diferentes tamaños
        const generateSrcset = async (originalBlob, originalWidth, originalHeight) => {
          let sizesToGenerate;
          if (quality === 1) {
            // Si la calidad es 1, solo genera el tamaño original en WebP
            sizesToGenerate = [originalWidth];
          } else {
            // De lo contrario, genera un conjunto de tamaños responsivos
            sizesToGenerate = [320, 640, 960, 1280];
            // Añadir el ancho original si no está ya presente y es mayor que el máximo actual
            if (originalWidth && !sizesToGenerate.includes(originalWidth) && originalWidth > sizesToGenerate[sizesToGenerate.length - 1]) {
              sizesToGenerate.push(originalWidth);
              sizesToGenerate.sort((a, b) => a - b);
            }
          }
          
          const srcsetPromises = sizesToGenerate.map(async (size) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const bitmap = await createImageBitmap(originalBlob);
            
            // Calcular dimensiones manteniendo la proporción
            const ratio = bitmap.width / bitmap.height;
            const newWidth = size;
            const newHeight = Math.round(size / ratio);
            
            canvas.width = newWidth;
            canvas.height = newHeight;
            ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
            
            const webpBlob = await new Promise(resolve => {
              canvas.toBlob(resolve, 'image/webp', quality);
            });
            
            return `${URL.createObjectURL(webpBlob)} ${size}w`;
          });
          
          return (await Promise.all(srcsetPromises)).join(', ');
        };

        if (window.createImageBitmap) {
          const bitmap = await createImageBitmap(blob);
          const canvas = document.createElement('canvas');
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(bitmap, 0, 0);
          
          // Generar srcset
          const newSrcset = await generateSrcset(blob, bitmap.width, bitmap.height);
          setSrcset(newSrcset);
          
          // Convertir a WebP con calidad configurable
          const webpBlob = await new Promise(resolve => {
            canvas.toBlob(resolve, 'image/webp', quality);
          });
          
          const webpUrl = URL.createObjectURL(webpBlob);
          setImageSrc(webpUrl);
          
          // Guardar en caché
          const reader = new FileReader();
          reader.onloadend = () => {
            localStorage.setItem(`img_cache_${src}`, reader.result);
          };
          reader.readAsDataURL(webpBlob);
        } else {
          // Fallback para navegadores que no soportan WebP
          const url = URL.createObjectURL(blob);
          setImageSrc(url);
          
          // Guardar en caché
          const reader = new FileReader();
          reader.onloadend = () => {
            localStorage.setItem(`img_cache_${src}`, reader.result);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.error('Error al cargar la imagen:', error);
        setImageSrc(src); // Fallback a la imagen original
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
  }, [src, quality]);

  return (
    <div
      className={`optimized-image-container ${isLoading ? 'loading' : ''}`}
      style={fixedContainer && width && height ? { width: width + 'px', height: height + 'px' } : {}}
    >
      {isLoading && <div className="image-placeholder" />}
      <img
        src={imageSrc}
        srcSet={srcset}
        sizes={sizes || '100vw'}
        alt={alt}
        className={`optimized-image ${className || ''}`}
        width={width}
        height={height}
        style={width && height ? { width: '100%', height: '100%', objectFit: 'contain' } : {}}
        loading="lazy"
        decoding="async"
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

export default OptimizedImage; 